'use client';

import { cn } from '@popow/infra/lib/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import * as React from 'react';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

type DialogHeaderAlign = 'center' | 'left';

type DialogHeaderProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * Header alignment. Prefer this over className inspection.
   * - center: [spacer][content][close]
   * - left:   [content][close]
   */
  align?: DialogHeaderAlign;
  /**
   * Optional attribute for consumers; DialogContent may read this as a fallback.
   */
  'data-align'?: DialogHeaderAlign;
};

const getComponentDisplayName = (component: unknown): string | undefined => {
  if (typeof component === 'function') {
    return (component as { displayName?: string }).displayName;
  }
  if (typeof component === 'object' && component !== null) {
    return (component as { displayName?: string }).displayName;
  }
  return undefined;
};

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // Popow DS: use semantic dimmer token (avoid hardcoded rgba)
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-semantic-material-dimmer/52 data-[state=closed]:animate-out data-[state=open]:animate-in',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  const childrenArray = React.Children.toArray(children);
  const headerIndex = childrenArray.findIndex(child => {
    if (!React.isValidElement(child)) {
      return false;
    }
    // Prefer stable identity comparison; fall back to displayName for safety/compat.
    return child.type === DialogHeader || getComponentDisplayName(child.type) === 'DialogHeader';
  });

  const headerAlign: DialogHeaderAlign =
    headerIndex >= 0 && React.isValidElement<DialogHeaderProps>(childrenArray[headerIndex])
      ? (() => {
          const headerEl = childrenArray[headerIndex] as React.ReactElement<DialogHeaderProps>;
          const alignProp = headerEl.props.align;
          const dataAlign = headerEl.props['data-align'];

          if (alignProp) {
            return alignProp;
          }
          if (dataAlign) {
            return dataAlign;
          }

          // Back-compat fallback: infer from className.
          const headerClassName = headerEl.props.className;
          return headerClassName?.includes('text-left') || headerClassName?.includes('items-start')
            ? 'left'
            : 'center';
        })()
      : 'center';
  const isHeaderLeftAligned = headerAlign === 'left';

  const closeButton = (
    <DialogPrimitive.Close
      className={cn(
        // Close: roomy touch target + less "circle-y" hover shape
        'inline-flex h-12 w-12 items-center justify-center rounded-lg text-semantic-label-alternative/61 transition-colors hover:bg-semantic-bg-normal-alternative hover:text-semantic-label-normal focus-visible:outline-none disabled:pointer-events-none',
      )}
    >
      <Cross2Icon className="h-5 w-5" />
      <span className="sr-only">Close</span>
    </DialogPrimitive.Close>
  );

  const shouldInjectCloseIntoHeader = headerIndex >= 0;
  // Dialog stories/components often place <DialogDescription /> inside <DialogHeader />.
  // But in Popow DS, the header "top bar" should only align [title][close], and description
  // should flow under the header (like the visual spec).
  const extractedDescriptions: React.ReactNode[] = [];
  const injectedChildrenBase = shouldInjectCloseIntoHeader
    ? childrenArray.map((child, idx) => {
        if (idx !== headerIndex) {
          return child;
        }
        if (!React.isValidElement(child)) {
          return child;
        }

        const headerEl = child as React.ReactElement<DialogHeaderProps>;
        const headerChildren = React.Children.toArray(headerEl.props.children);
        const headerMainChildren: React.ReactNode[] = [];

        headerChildren.forEach(headerChild => {
          if (!React.isValidElement(headerChild)) {
            headerMainChildren.push(headerChild);
            return;
          }

          // Prefer stable identity comparison against our wrapper component.
          // Fallback to displayName comparison for safety across environments.
          const isDescription =
            headerChild.type === DialogDescription ||
            getComponentDisplayName(headerChild.type) === DialogDescription.displayName ||
            getComponentDisplayName(headerChild.type) === DialogPrimitive.Description.displayName;
          if (isDescription) {
            extractedDescriptions.push(headerChild);
            return;
          }

          headerMainChildren.push(headerChild);
        });

        return React.cloneElement(headerEl, {
          children: (
            <>
              {/* Left-aligned header: [content][close] */}
              {isHeaderLeftAligned ? (
                <>
                  <div className="col-start-1 flex flex-col items-start gap-2 text-left">
                    {headerMainChildren}
                  </div>
                  <div className="col-start-2 flex items-center justify-end">{closeButton}</div>
                </>
              ) : (
                <>
                  {/* Centered header: [spacer][content][close] */}
                  <div className="col-start-1" aria-hidden="true" />
                  <div className="col-start-2 flex flex-col items-center gap-2 text-center">
                    {headerMainChildren}
                  </div>
                  <div className="col-start-3 flex items-center justify-end">{closeButton}</div>
                </>
              )}
            </>
          ),
        });
      })
    : childrenArray;

  const injectedChildren =
    extractedDescriptions.length > 0 && headerIndex >= 0
      ? [
          ...injectedChildrenBase.slice(0, headerIndex + 1),
          <div key="__dialog_extracted_description__" className="space-y-2">
            {extractedDescriptions}
          </div>,
          ...injectedChildrenBase.slice(headerIndex + 1),
        ]
      : injectedChildrenBase;

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // Popow DS: fill-only surface (no container stroke)
          // Airbnb-ish: tighter rhythm; header/footer handle dividers & spacing
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed top-[50%] left-[50%] z-50 grid w-[calc(100%-32px)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-3 rounded-[20px] bg-semantic-bg-normal-normal px-5 py-3 shadow-sm duration-200 data-[state=closed]:animate-out data-[state=open]:animate-in sm:w-full',
          className,
        )}
        {...props}
      >
        {injectedChildren}
        {!shouldInjectCloseIntoHeader && (
          <div className="absolute top-3 right-3">
            {/* Fallback for dialogs without a header */}
            {closeButton}
          </div>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, align, ...props }: DialogHeaderProps) => {
  // Back-compat fallback: infer align from className when align prop isn't provided.
  const inferredAlign: DialogHeaderAlign =
    align ??
    (className?.includes('text-left') || className?.includes('items-start') ? 'left' : 'center');

  return (
    <div
      data-align={inferredAlign}
      className={cn(
        // Centered "top bar" (no divider under header)
        // Default: centered (spacer / content / close)
        // Header padding: reduce left/right by 8px (20px -> 12px)
        '-mx-5 grid min-h-12 items-center px-3',
        inferredAlign === 'left' ? 'grid-cols-[1fr_48px]' : 'grid-cols-[48px_1fr_48px] text-center',
        className,
      )}
      {...props}
    />
  );
};
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // Full-bleed footer divider + tighter padding (Airbnb-ish)
      '-mx-5 mt-3 border-semantic-line-normal-normal/12 border-t px-5 pt-3',
      'flex items-center justify-end gap-2',
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    // Popow DS typography: Headline 2 / Medium
    className={cn('font-mona-sans text-headline-2-medium text-semantic-label-strong', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(
      // NOTE: semantic label tokens require explicit opacity (see globals.css aliases)
      'font-mona-sans text-body-2-reading-regular text-semantic-label-neutral/88',
      className,
    )}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
